/**
 * seriesExtractors.js — pure helpers that turn recorded session metrics into chart series,
 * replay frames and statistics. No React; everything here works on the parsed API payloads.
 *
 * GET /api/sessions/:id/:metric?format=json returns (backend models, epoch-ms timestamps):
 *   skeleton     [{ timestamp, data: [{ bodyPart, position:{x,y,z}, orientation:{x,y,z,w} }] }]
 *   robotic      [{ timestamp, data: { R1|R2|B1: { Value, Values, dataValue, Dimension, Time } } }]
 *   biometric    [{ timestamp, data: { HR|GSR: { Value, … } } }]
 *   hands        [{ timestamp, leftHand:{x,y,z}, rightHand:{x,y,z} }]
 *   parameter    [{ timestamp, name, data: [{ name, value_, parm_type, min_, max_, strings }] }]
 *   sessionevent [{ timestamp, Value, dataType, eventType?, color? }]  (eventType / color: EGDL 4.1, both optional)
 */
import { toUniformGrid } from './charts';
import { resolveJointName } from './skeletonConstants';

export const METRICS = [
    { key: 'skeleton', label: 'Skeleton', chart: true },
    { key: 'robotic', label: 'Robotic', chart: true },
    { key: 'biometric', label: 'Biometric', chart: true },
    { key: 'hands', label: 'Hands', chart: true },
    { key: 'parameter', label: 'Parameters', chart: false },
    { key: 'sessionevent', label: 'Events', chart: false },
];
export const METRIC_KEYS = METRICS.map((m) => m.key);
export const CHARTABLE_METRICS = METRICS.filter((m) => m.chart).map((m) => m.key);
export const metricLabel = (key) => METRICS.find((m) => m.key === key)?.label || key;

/* ── small formatters ─────────────────────────────────────────────────── */

/** Epoch ms from a number, numeric string or ISO string. */
export function toMs(x) {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return Number.isFinite(x) ? x : null;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
}

/** Seconds → "m:ss.s" (replay clock). */
export function fmtClock(s, dp = 1) {
    const v = Math.max(0, s || 0);
    const m = Math.floor(v / 60);
    const r = v - m * 60;
    const str = r.toFixed(dp);
    return `${m}:${r < 10 ? '0' : ''}${str}`;
}

/** Milliseconds → "m:ss" (or "h:mm:ss"). */
export function fmtDuration(ms) {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
    const sec = Math.round(ms / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const ss = String(s).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** "RightShoulderAbductionAngle" → "Right shoulder abduction" ; "HandRight" → "Hand right". */
export function humanize(name) {
    if (!name) return '';
    const words = String(name)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .trim()
        .split(/\s+/);
    if (words.length > 1 && /^angle$/i.test(words[words.length - 1])) words.pop();
    return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : /^[A-Z0-9]+$/.test(w) && w.length <= 3 ? w : w.toLowerCase())).join(' ');
}

const num = (x) => (typeof x === 'number' ? x : x == null || x === '' ? null : Number(x));
const fin = (x) => {
    const n = num(x);
    return Number.isFinite(n) ? n : null;
};

/* ── payload parsing ──────────────────────────────────────────────────── */

/** Parse a metric payload (string or array). Returns an array, or null when it isn't one. */
export function parseMetric(raw) {
    let d = raw;
    if (typeof d === 'string') {
        if (!d.trim()) return [];
        try { d = JSON.parse(d); } catch { return null; }
    }
    return Array.isArray(d) ? d : null;
}

/** Sorted timestamps (ms) of a metric array. */
function stamps(arr) {
    const out = [];
    (arr || []).forEach((f) => {
        const t = toMs(f?.timestamp);
        if (t != null) out.push(t);
    });
    return out.sort((a, b) => a - b);
}

/** Median interval between samples, in ms (null with fewer than 2 samples). */
export function medianInterval(arr) {
    const ts = stamps(arr);
    if (ts.length < 2) return null;
    const step = Math.max(1, Math.floor(ts.length / 600));
    const d = [];
    for (let i = step; i < ts.length; i += step) {
        const x = (ts[i] - ts[i - step]) / step;
        if (x > 0) d.push(x);
    }
    if (!d.length) return null;
    d.sort((a, b) => a - b);
    return d[Math.floor(d.length / 2)];
}

/**
 * Shared time base for every chart and the replay.
 * data: { [metricKey]: array } (loaded metrics only). Returns null when nothing has a timestamp.
 * { t0 (ms), hz, n, duration (s, = (n-1)/hz), rates: { key: Hz } }
 */
export function buildTimeline(data, session) {
    let lo = Infinity;
    let hi = -Infinity;
    Object.values(data).forEach((arr) => {
        (arr || []).forEach((f) => {
            const t = toMs(f?.timestamp);
            if (t == null) return;
            if (t < lo) lo = t;
            if (t > hi) hi = t;
        });
    });
    if (lo === Infinity) {
        const a = toMs(session?.startTime);
        const b = toMs(session?.endTime);
        if (a == null || b == null || b <= a) return null;
        lo = a;
        hi = b;
    }
    const span = Math.max(1, (hi - lo) / 1000);
    const rates = {};
    let rate = 1;
    CHARTABLE_METRICS.forEach((k) => {
        const mi = data[k] ? medianInterval(data[k]) : null;
        if (mi) {
            rates[k] = 1000 / mi;
            rate = Math.max(rate, rates[k]);
        }
    });
    let hz = Math.min(10, Math.max(1, Math.ceil(rate)));
    if (span * hz > 12000) hz = 12000 / span;
    const n = Math.max(2, Math.round(span * hz) + 1);
    return { t0: lo, hz, n, duration: (n - 1) / hz, rates };
}

/** Bin count for a timescale ('full' | '1s' | '10s') on a timeline. */
export function binFor(timescale, hz) {
    if (timescale === '1s') return Math.max(1, Math.round(hz));
    if (timescale === '10s') return Math.max(1, Math.round(hz * 10));
    return 1;
}

/* ── skeleton helpers ─────────────────────────────────────────────────── */

/** Every bodyPart present in the skeleton frames, sorted. */
export function skeletonJoints(frames) {
    const parts = new Set();
    (frames || []).forEach((f) => {
        if (Array.isArray(f?.data)) f.data.forEach((d) => { if (d?.bodyPart) parts.add(d.bodyPart); });
    });
    return [...parts].sort();
}

/** Default joint to chart: the right hand if tracked, else the right shoulder, else the first. */
export function defaultJoint(joints) {
    const pick = (c) => joints.find((j) => resolveJointName(j) === c);
    return pick('HandRight') || pick('WristRight') || pick('ShoulderRight') || joints[0] || '';
}

/* ── series catalog ───────────────────────────────────────────────────── */

const firstKeys = (frames, field = 'data') => {
    const keys = new Set();
    (frames || []).slice(0, 50).forEach((f) => {
        const o = f?.[field];
        if (o && typeof o === 'object' && !Array.isArray(o)) Object.keys(o).forEach((k) => keys.add(k));
    });
    return [...keys];
};

const BIO_LABEL = { HR: 'Heart rate', GSR: 'GSR' };

/**
 * Series a metric offers: [{ key, group, label, unit, axis, dp }].
 * Skeleton series follow the chosen joint; hands are left/right × x/y/z.
 */
export function seriesCatalog(metric, frames, joint) {
    if (!frames || !frames.length) return [];
    const jl = humanize(joint) || 'Joint';
    switch (metric) {
        case 'skeleton':
            return [
                ...['x', 'y', 'z'].map((a) => ({ key: `sk:pos:${a}`, group: 'skeleton', label: `${jl} ${a.toUpperCase()}`, item: `Position ${a.toUpperCase()}`, unit: '', axis: 'left', dp: 3 })),
                ...['x', 'y', 'z', 'w'].map((a) => ({ key: `sk:rot:${a}`, group: 'skeleton', label: `${jl} rotation ${a.toUpperCase()}`, item: `Rotation ${a.toUpperCase()}`, unit: '', axis: 'left', dp: 3 })),
            ];
        case 'robotic':
            return firstKeys(frames).sort().map((ch) => ({ key: `r:${ch}`, group: 'robotic', label: ch, item: ch, unit: '', axis: 'left', dp: 2 }));
        case 'biometric':
            return firstKeys(frames).sort((a, b) => (a === 'HR' ? -1 : b === 'HR' ? 1 : a.localeCompare(b))).map((ch) => ({
                key: `b:${ch}`, group: 'biometric', label: BIO_LABEL[ch] || ch, item: ch === 'HR' ? 'Heart rate (bpm)' : BIO_LABEL[ch] || ch,
                unit: ch === 'HR' ? 'bpm' : '', axis: ch === 'GSR' ? 'right' : 'left', dp: ch === 'HR' ? 0 : 2,
            }));
        case 'hands':
            return ['left', 'right'].flatMap((h) => ['x', 'y', 'z'].map((a) => ({
                key: `h:${h}:${a}`, group: 'hands', label: `Hands · ${h} ${a.toUpperCase()}`, item: `${h === 'left' ? 'Left' : 'Right'} hand ${a.toUpperCase()}`, unit: '', axis: 'left', dp: 3,
            })));
        default:
            return [];
    }
}

/** Timestamped samples [{ t (s from t0), v }] for one catalog key. */
export function samplesFor(def, frames, joint, t0) {
    const out = [];
    const [, a, b] = def.key.split(':');
    (frames || []).forEach((f) => {
        const ts = toMs(f?.timestamp);
        if (ts == null) return;
        let v = null;
        if (def.group === 'skeleton') {
            const jd = Array.isArray(f.data) ? f.data.find((d) => d && d.bodyPart === joint) : null;
            v = fin((a === 'pos' ? jd?.position : jd?.orientation)?.[b]);
        } else if (def.group === 'robotic' || def.group === 'biometric') {
            const c = f.data?.[a];
            v = fin(c && typeof c === 'object' ? c.Value ?? c.dataValue : c);
        } else if (def.group === 'hands') {
            v = fin(f[a === 'left' ? 'leftHand' : 'rightHand']?.[b]);
        }
        if (v != null) out.push({ t: (ts - t0) / 1000, v });
    });
    return out;
}

/**
 * Samples → the shared uniform grid. Empty bins take the nearest sample within `tolBins`
 * (same rule as the old nearest-within-500 ms alignment), so slower streams still read at
 * every grid point without inventing values across real gaps.
 */
export function toGrid(samples, timeline, tolBins = 0) {
    const { hz, duration } = timeline;
    const a = toUniformGrid(samples, 0, duration, hz);
    if (tolBins <= 0) return a;
    const n = a.length;
    const prev = new Array(n);
    let last = -Infinity;
    for (let i = 0; i < n; i++) {
        if (a[i] != null) last = i;
        prev[i] = last;
    }
    const out = a.slice();
    let next = Infinity;
    for (let i = n - 1; i >= 0; i--) {
        if (a[i] != null) { next = i; continue; }
        const dp = i - prev[i];
        const dn = next - i;
        if (dp <= dn && dp <= tolBins) out[i] = a[prev[i]];
        else if (dn <= tolBins) out[i] = a[next];
    }
    return out;
}

/** Nearest-fill tolerance (bins) for a metric: 1.5 sample intervals, at least 0.5 s. */
export function tolFor(metric, timeline) {
    const r = timeline.rates[metric];
    const sec = Math.max(0.5, r ? 1.5 / r : 0.5);
    return Math.ceil(sec * timeline.hz);
}

/* ── events / parameters ──────────────────────────────────────────────── */

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Session events → [{ t (s), label, type, color }] on the timeline. type and color are the game's
 * (eventType, color; '' when it gave none); the value's .NET type (dataType) is not a type a person reads.
 */
export function eventsOn(frames, t0) {
    return (frames || [])
        .map((e) => {
            const ts = toMs(e?.timestamp);
            if (ts == null) return null;
            const label = e.Value != null && e.Value !== '' ? String(e.Value) : '—';
            // the server keeps at most 40 characters; a file written another way is held to the same
            const type = typeof e.eventType === 'string' ? Array.from(e.eventType.trim()).slice(0, 40).join('').trim() : '';
            const color = typeof e.color === 'string' && HEX.test(e.color) ? e.color.toLowerCase() : '';
            return { t: (ts - t0) / 1000, label, type, color };
        })
        .filter(Boolean)
        .sort((x, y) => x.t - y.t)
        .map((e, id) => ({ ...e, id })); // a row's own key, kept when the legend hides others
}

export function fmtParamValue(v) {
    if (v == null || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'On' : 'Off';
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(+v.toFixed(3));
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

/* ── statistics ───────────────────────────────────────────────────────── */

/** results.jointsData → [{ name, min, max, rom }] (array or legacy object shape). */
export function jointAngleStats(jointsData) {
    let rows = [];
    if (Array.isArray(jointsData)) rows = jointsData;
    else if (jointsData && typeof jointsData === 'object') rows = Object.entries(jointsData).map(([name, v]) => ({ name, ...(v || {}) }));
    return rows
        .map((j) => {
            const min = fin(j?.angleMin ?? j?.min);
            const max = fin(j?.angleMax ?? j?.max);
            return { name: j?._name || j?.name || j?.jointName || '', min, max, rom: min != null && max != null ? max - min : null };
        })
        .filter((r) => r.name);
}

/** results.bonesData → per-axis rows [{ name, axis, min, max, range }]. */
export function bonePositionStats(bonesData) {
    let rows = [];
    if (Array.isArray(bonesData)) rows = bonesData;
    else if (bonesData && typeof bonesData === 'object') rows = Object.entries(bonesData).map(([name, v]) => ({ name, ...(v || {}) }));
    const out = [];
    rows.forEach((b) => {
        const name = b?._name || b?.name || b?.boneName;
        const lo = b?.positionMin || b?.bonePositionMin;
        const hi = b?.positionMax || b?.bonePositionMax;
        if (!name || (!lo && !hi)) return;
        ['x', 'y', 'z'].forEach((a) => {
            const min = fin(lo?.[a]);
            const max = fin(hi?.[a]);
            if (min == null && max == null) return;
            out.push({ name, axis: a.toUpperCase(), min, max, range: min != null && max != null ? max - min : null });
        });
    });
    return out;
}

/** Recorded skeleton frames → per-joint, per-axis [{ name, axis, min, max, mean }]. */
export function jointPositionStats(frames) {
    const acc = new Map();
    (frames || []).forEach((f) => {
        if (!Array.isArray(f?.data)) return;
        f.data.forEach((d) => {
            if (!d?.bodyPart || !d.position) return;
            let s = acc.get(d.bodyPart);
            if (!s) {
                s = { x: [Infinity, -Infinity, 0, 0], y: [Infinity, -Infinity, 0, 0], z: [Infinity, -Infinity, 0, 0] };
                acc.set(d.bodyPart, s);
            }
            ['x', 'y', 'z'].forEach((a) => {
                const v = fin(d.position[a]);
                if (v == null) return;
                const r = s[a];
                if (v < r[0]) r[0] = v;
                if (v > r[1]) r[1] = v;
                r[2] += v;
                r[3] += 1;
            });
        });
    });
    const out = [];
    [...acc.keys()].sort().forEach((name) => {
        const s = acc.get(name);
        ['x', 'y', 'z'].forEach((a) => {
            const [min, max, sum, n] = s[a];
            if (!n) return;
            out.push({ name, axis: a.toUpperCase(), min, max, mean: sum / n });
        });
    });
    return out;
}
